/**
 * Investor form validation
 */

import { UserRole } from '@/types/user';
import { errorMessages } from './errorMessages';
import type { ValidationResult } from './types';
import { validators } from './validators';

export type InvestorFormData = {
  firstName: string;
  middleName: string;
  lastname: string;
  dateOfBirth: string;
  email: string;
  phone: string;
  countryCode: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  password: string;
  repeat: string;
  acceptedTerms: boolean;
  ein: string;
  companyName: string;
  user_role: UserRole.INVESTOR_INDIVIDUAL | UserRole.INVESTOR_COMPANY;
};

export const validateInvestorForm = (form: InvestorFormData, includeTerms = true): ValidationResult => {
  const errors: Partial<Record<keyof InvestorFormData, string[]>> = {};

  // Name fields (can be individual or company)
  if (form.user_role === UserRole.INVESTOR_INDIVIDUAL) {
    const firstNameError = errorMessages.name(form.firstName, 'First name');
    if (firstNameError) errors.firstName = [firstNameError];

    const lastnameError = errorMessages.name(form.lastname, 'Lastname');
    if (lastnameError) errors.lastname = [lastnameError];

    // Middle name is optional but must be valid if provided
    if (form.middleName && !validators.name(form.middleName)) {
      errors.middleName = ['Middle name contains invalid characters'];
    }
  } else {
    const companyError = errorMessages.required(form.companyName, 'Company name');
    if (companyError) errors.companyName = [companyError];
  }

  // Contact fields
  const emailError = errorMessages.email(form.email);
  if (emailError) errors.email = [emailError];

  const phoneError = errorMessages.phone(form.phone);
  if (phoneError) errors.phone = [phoneError];

  // Personal info
  const validateAge = form.user_role === UserRole.INVESTOR_INDIVIDUAL;
  const dobError = errorMessages.dateOfBirth(form.dateOfBirth, validateAge);
  if (dobError) errors.dateOfBirth = [dobError];

  if (form.user_role === UserRole.INVESTOR_COMPANY) {
    const einError = errorMessages.ein(form.ein);
    if (einError) errors.ein = [einError];
  }

  // Address fields
  const addressError = errorMessages.required(form.address1, 'Address');
  if (addressError) errors.address1 = [addressError];

  const cityError = errorMessages.city(form.city);
  if (cityError) errors.city = [cityError];

  const stateError = errorMessages.state(form.state);
  if (stateError) errors.state = [stateError];

  const zipError = errorMessages.zip(form.zip);
  if (zipError) errors.zip = [zipError];

  // Password fields
  const passwordError = errorMessages.password(form.password);
  if (passwordError) errors.password = [passwordError];

  const confirmError = errorMessages.passwordConfirm(form.password, form.repeat);
  if (confirmError) errors.repeat = [confirmError];

  // Terms
  if (includeTerms && !form.acceptedTerms) {
    errors.acceptedTerms = ['You must agree to the Privacy Policy'];
  }

  const errorList = Object.values(errors).flat();

  return {
    isValid: errorList.length === 0,
    errors,
    firstError: errorList[0],
  };
};
